import { useCallback } from 'react';
import { useTranslation } from '~/_shared/utils/hooks';
import { shortenDemographicsName } from '~/sidebar/sidebarApps/mapTools/boundary/fill/boundaryFill.helpers';
import { useBoundaryGroupDemographics } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryGroupDemographics';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';

export type MapBoundaryLabelLegendCallback = (boundaryTerritoryGroup: BoundaryTerritoryGroup) => string | null;
const ColumnNameLengthLimit = 25;
const shortenColumnName = (columnName: string): string => {
  if (columnName.length < ColumnNameLengthLimit) {
    return columnName;
  }
  return columnName.substring(0, ColumnNameLengthLimit - 1) + '…';
};

export const useMapBoundaryLabelLegend = () => {
  const { getDemographic } = useBoundaryGroupDemographics();
  const spreadsheetColumns = useSpreadsheetColumns();
  const [t] = useTranslation();

  const getMapBoundaryLabelLegend: MapBoundaryLabelLegendCallback = useCallback((boundaryTerritoryGroup: BoundaryTerritoryGroup) => {
    const { settings } = boundaryTerritoryGroup;
    const {
      boundaryTerritoryType, demographicId, bucketColumnId, labelValueConfig: {
        isValueCustom,
        labelValueType,
        labelValueColumnId,
        labelValueDemographicId,
      },
    } = settings;

    const selectedBoundaryTerritoryType = isValueCustom ? labelValueType : boundaryTerritoryType;
    const selectedDemographic = isValueCustom ? labelValueDemographicId : demographicId;
    const selectedLabelValueColumnId = isValueCustom ? labelValueColumnId : bucketColumnId;

    // For manual type, return null
    if (selectedBoundaryTerritoryType === BoundaryTerritoryType.Manual) {
      return null;
    }

    // For density type, return "Locations"
    if (selectedBoundaryTerritoryType === BoundaryTerritoryType.Density) {
      return t('Locations');
    }

    // For demographic type, find and return demographic name
    if ((selectedBoundaryTerritoryType === BoundaryTerritoryType.Demographic) && selectedDemographic) {
      const demographic = getDemographic(boundaryTerritoryGroup.boundaryGroupId, selectedDemographic);
      return (demographic && shortenDemographicsName(demographic.name)) ?? null;
    }

    // For spreadsheet column types (numeric, groups), find and return column name
    if (selectedLabelValueColumnId && ((selectedBoundaryTerritoryType === BoundaryTerritoryType.Numeric) || (selectedBoundaryTerritoryType === BoundaryTerritoryType.Groups))) {
      const column = spreadsheetColumns.find(col => col.id === selectedLabelValueColumnId);
      return (column && shortenColumnName(column.name)) ?? null;
    }

    return null;
  }, [getDemographic, spreadsheetColumns, t]);

  return { getMapBoundaryLabelLegend };
};
